/**
 * Settings Slice - Global application settings
 * Includes company logo that appears in every watermark.
 *
 * Company logo is persisted in TWO places so it survives device changes
 * for the same user account:
 *   1. localStorage (instant reads, offline capable)
 *   2. users.company_logo column on the server (syncs across devices)
 *
 * `setCompanyLogo` writes both. `setCompanyLogoLocal` skips the server call
 * and is used by userSlice.login() when seeding from the server's response.
 *
 * Note on same-tab sync: the `pinCam:settingsChanged` custom event is dispatched
 * by the settings UI (SettingsBottomSheet) when the user closes the sheet, not
 * by this slice. The slice only owns state + persistence; it intentionally does
 * not emit that event, matching the existing wiring that CameraScreen listens to.
 */

import { safeStorage } from '@/utils/safeStorage';
import { logger } from '@/utils/logger';
import api from '@/services/api/mobileApiService';

const SETTINGS_STORAGE_KEY = 'app_settings';

export interface SettingsState {
  companyLogo: string | null; // Base64 data URI for company logo
  showMicButtonOnCamera: boolean; // Show mic toggle button on camera screen
  showAnnotationButtonOnCamera: boolean; // Show annotation toggle button on camera screen
}

export interface SettingsActions {
  setCompanyLogo: (logo: string | null) => void;
  setCompanyLogoLocal: (logo: string | null) => void;
  setShowMicButtonOnCamera: (show: boolean) => void;
  setShowAnnotationButtonOnCamera: (show: boolean) => void;
  loadSettings: () => void;
}

/**
 * Read settings from localStorage at boot, falling back to sensible defaults
 * (both camera buttons shown) when nothing is stored or the value is corrupt.
 */
const loadInitialSettings = (): SettingsState => {
  try {
    const stored = safeStorage.getItem(SETTINGS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<SettingsState>;
      return {
        companyLogo: parsed.companyLogo || null,
        showMicButtonOnCamera: parsed.showMicButtonOnCamera ?? true, // Default: show
        showAnnotationButtonOnCamera: parsed.showAnnotationButtonOnCamera ?? true, // Default: show
      };
    }
  } catch (error) {
    logger.warn('Failed to load settings from storage', { error });
  }
  return {
    companyLogo: null,
    showMicButtonOnCamera: true,
    showAnnotationButtonOnCamera: true,
  };
};

export const settingsSlice: SettingsState = loadInitialSettings();

/** Persist only the settings-owned fields back to localStorage. */
const saveSettings = (state: SettingsState): void => {
  try {
    safeStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify({
      companyLogo: state.companyLogo,
      showMicButtonOnCamera: state.showMicButtonOnCamera,
      showAnnotationButtonOnCamera: state.showAnnotationButtonOnCamera,
    }));
  } catch (error) {
    logger.error('Failed to save settings to storage', { error });
  }
};

// The store hands `set`/`get` to slices with deliberately loose signatures:
// fully typing them against AppState creates a circular reference
// (AppState → Actions → set(AppState) → AppState), so the factory keeps the
// permissive contract. We recover type-safety by annotating each immer draft as
// `SettingsState` and narrowing `get()`'s result where it's read below.
type SettingsDraft = SettingsState;
// What this slice actually reads off the combined store via `get()`.
type SettingsView = SettingsState & { isAuthenticated?: boolean };

export const settingsActions = (
  set: (fn: (state: any) => void) => void,
  get: () => any,
): SettingsActions => ({
  setCompanyLogo: (logo: string | null) => {
    // Update local state + localStorage synchronously so the UI reacts instantly
    set((state: SettingsDraft) => {
      state.companyLogo = logo;
    });
    saveSettings(get());

    // Keep the cached user_data in sync so the next page reload doesn't re-seed
    // from a stale login response in initializeAuth().
    try {
      const raw = safeStorage.getItem('user_data');
      if (raw) {
        const u = JSON.parse(raw) as Record<string, unknown>;
        u.company_logo = logo;
        safeStorage.setItem('user_data', JSON.stringify(u));
      }
    } catch (err) {
      logger.warn('Failed to update cached user_data with new company logo', { err });
    }

    // Fire-and-forget sync to the server. Only meaningful when authenticated;
    // if the call fails the local value is already saved, so we don't block UX.
    const state = get() as SettingsView;
    if (state?.isAuthenticated) {
      api.put('/auth/me/company-logo', { company_logo: logo })
        .then(() => logger.info('Company logo synced to server'))
        .catch((err: unknown) => logger.warn('Failed to sync company logo to server', {
          // Preserve the original logged shape (err?.message) while staying off `any`.
          message: (err as { message?: string } | null | undefined)?.message,
        }));
    } else {
      logger.info('Company logo saved locally (not authenticated; will sync on next save after login)');
    }
  },

  // Seeds localStorage from the server's login response without echoing back.
  // Used by userSlice.login() to prime state on a new device.
  setCompanyLogoLocal: (logo: string | null) => {
    set((state: SettingsDraft) => {
      state.companyLogo = logo;
    });
    saveSettings(get());
  },

  setShowMicButtonOnCamera: (show: boolean) => {
    set((state: SettingsDraft) => {
      state.showMicButtonOnCamera = show;
    });
    saveSettings(get());
  },

  setShowAnnotationButtonOnCamera: (show: boolean) => {
    set((state: SettingsDraft) => {
      state.showAnnotationButtonOnCamera = show;
    });
    saveSettings(get());
  },

  loadSettings: () => {
    const settings = loadInitialSettings();
    set((state: SettingsDraft) => {
      state.companyLogo = settings.companyLogo;
      state.showMicButtonOnCamera = settings.showMicButtonOnCamera;
      state.showAnnotationButtonOnCamera = settings.showAnnotationButtonOnCamera;
    });
  },
});
